// b8: Ruuvi-givarna och vinden från väderdata.
//   - Hälsa: givarnas hälsa, med en givare som inte har hörts på 30 minuter och ett batteri
//     under 2,5 V. Ingen push: kyla ger lägre spänning en stund.
//   - Hälsa, för en båt: vinden från väderdata, med källan och tiden. Den är inte båtens givare.
//   - Inställningar → Givare, bara för ägaren: givarna som navet hör, de starkaste först, och
//     deras plats.
//   - b9: batteriet i procent för BTHome och Inkbird, krypterade givare utan nyckel, och kortet
//     Badtemperatur.

import {
  type Badtemperatur,
  dagOchTid,
  GIVARROLLNAMN,
  type Givarroll,
  givarnamn,
  type Halsa,
  type Platsinstallningar,
  sedan,
  tal,
  type Vadervarde,
  type Vadervind,
  type Valdgivare,
} from "@batkoll/gemensam";
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { api } from "../api.ts";
import { Kort, Rad } from "../komponenter.tsx";
import { tal1 } from "../visa.ts";

/** En givare som inte har hörts så här länge visas på Hälsa. */
export const TYST_MS = 30 * 60_000;
/** Ett batteri under så här många volt visas på Hälsa. */
export const LAGT_BATTERI_V = 2.5;
/** b9: och under så här många procent, för givare som säger batteriet i procent. */
export const LAGT_BATTERI_PROCENT = 15;

/** Givarens plats i en text: "kabin", "rummet Loft". */
export function givarensPlats(g: Pick<Valdgivare, "roll" | "namn">): string {
  return g.roll === "rum" ? (g.namn ?? "rum") : GIVARROLLNAMN[g.roll].toLowerCase();
}

/** Givarnas hälsa: när de hördes senast, och batteriet. */
export function Givarhalsa(props: { h: Halsa; nu: number }) {
  const { h, nu } = props;
  if (h.givare.length === 0) return null;
  const rader = h.givare.map((g) => {
    const batteri = h.signaler[`givare.batteri@${g.id}`];
    const procent = h.signaler[`givare.batteri_procent@${g.id}`];
    const signal = h.signaler[`givare.signal@${g.id}`];
    const senast = Math.max(batteri?.tid ?? 0, procent?.tid ?? 0, signal?.tid ?? 0) || null;
    const tyst = senast === null || nu - senast > TYST_MS;
    const v = typeof batteri?.varde === "number" ? batteri.varde : null;
    const pr = typeof procent?.varde === "number" ? procent.varde : null;
    const text = v !== null ? `${tal(v, 2)} V` : pr !== null ? `${tal(pr, 0)} %` : "–";
    const lagt = (v !== null && v < LAGT_BATTERI_V) || (pr !== null && pr < LAGT_BATTERI_PROCENT);
    return { g, senast, tyst, batteri: text, lagt };
  });
  const problem = rader.filter((r) => r.tyst || r.lagt);
  return (
    <Kort rubrik="Givarnas hälsa" ton={problem.length > 0 ? "varning" : undefined}>
      {rader.map((r) => (
        <div key={r.g.id} class="givare">
          <Rad namn={`${givarnamn(r.g.modell, r.g.id)}, ${givarensPlats(r.g)}`} gammalt={r.tyst}>
            {r.senast === null ? "har inte hörts" : `hördes ${sedan(r.senast, nu)}`}
          </Rad>
          <Rad namn="Batteri">
            {r.batteri}
            {r.lagt ? ", lågt" : ""}
          </Rad>
        </div>
      ))}
      {problem.map((r) => (
        <p key={r.g.id} class="liten varning-text">
          {r.tyst
            ? `${givarnamn(r.g.modell, r.g.id)} (${givarensPlats(r.g)}) har inte hörts på 30 minuter. Den kan vara för långt från navet, eller ha slut på batteriet.`
            : `Batteriet i ${givarnamn(r.g.modell, r.g.id)} (${givarensPlats(r.g)}) är lågt: ${r.batteri}. Kyla ger lägre spänning en stund, så byt det om det står kvar.`}
        </p>
      ))}
    </Kort>
  );
}

const RIKTNINGAR = ["N", "NO", "O", "SO", "S", "SV", "V", "NV"];

/** Vindriktningen: varifrån det blåser, till exempel "SV (225°)". */
export function riktning(grader: number | null): string {
  if (grader === null) return "–";
  const i = Math.round((((grader % 360) + 360) % 360) / 45) % 8;
  return `${RIKTNINGAR[i]} (${tal(grader, 0)}°)`;
}

function vind(v: Vadervarde): string {
  const medel = v["vader.vind_ms"];
  const byar = v["vader.byvind_ms"];
  if (medel === null && byar === null) return "–";
  return `${medel === null ? "–" : tal(medel, 0)} m/s, byar ${byar === null ? "–" : tal(byar, 0)} m/s`;
}

/** Vinden för båten från väderdata: inte båtens givare. */
export function Vindkort(props: { v: Vadervind; tidszon: string; nu: number }) {
  const { v, nu, tidszon } = props;
  const timmar = v.prognos?.timmar ?? [];
  const nu1 = timmar.filter((t) => t.tid <= nu).at(-1) ?? timmar[0];
  const hard = timmar.some(
    (t) =>
      (t["vader.vind_ms"] ?? 0) >= v.granser.kulingMs ||
      (t["vader.byvind_ms"] ?? 0) >= v.granser.byarMs,
  );
  const o = v.observation;
  return (
    <Kort rubrik="Vind" ton={hard ? "varning" : undefined} bred>
      <p class="liten">
        Enligt väderdata, inte uppmätt ombord.
        {v.position === "senast_kanda" ? " För navets senast kända position." : ""}
      </p>
      {!v.prognos &&
        !o &&
        (v.position === null || v.position === undefined ? (
          <p class="meddelande">
            Ingen väderdata. Båten behöver en position: ange den under Inställningar, eller vänta på
            navets GPS.
          </p>
        ) : (
          <p class="meddelande">Ingen väderdata än. Den hämtas för båtens position.</p>
        ))}
      {nu1 && (
        <div class="stort">
          {tal1(nu1["vader.vind_ms"], "m/s", 0)}
          <span class="liten">
            byar {tal1(nu1["vader.byvind_ms"], "m/s", 0)}, från{" "}
            {riktning(nu1["vader.vind_riktning"])}
          </span>
        </div>
      )}
      {o && (
        <Rad namn={`Observation: ${o.kalla}, ${o.station}, ${tal(o.avstandKm, 0)} km`}>
          {vind(o)}, från {riktning(o["vader.vind_riktning"])} · {dagOchTid(o.tid, tidszon, nu)}
        </Rad>
      )}
      {v.prognos && (
        <>
          <p class="liten">
            Prognos: {v.prognos.kalla}
            {v.prognos.hamtad !== null ? `, hämtad ${sedan(v.prognos.hamtad, nu)}` : ""}. Varning
            vid medelvind {tal(v.granser.kulingMs, 0)} m/s eller byar {tal(v.granser.byarMs, 0)} m/s
            inom ett dygn.
          </p>
          <ol class="vindprognos" aria-label="Vinden de närmaste 24 timmarna">
            {timmar
              .filter((t) => t.tid > nu - 3600_000)
              .slice(0, 24)
              .map((t) => (
                <li key={t.tid} class="vindtimme">
                  <span class="namn">{dagOchTid(t.tid, tidszon, nu)}</span>{" "}
                  <span class="varde">
                    {vind(t)}, {riktning(t["vader.vind_riktning"])}
                  </span>
                </li>
              ))}
          </ol>
        </>
      )}
    </Kort>
  );
}

const SIGNALSTYRKA = (dbm: number | null) =>
  dbm === null
    ? "–"
    : dbm >= -70
      ? `${dbm} dBm, bra`
      : dbm >= -85
        ? `${dbm} dBm, svag`
        : `${dbm} dBm, mycket svag`;

/** Inställningar → Givare, bara för ägaren. */
export function Givardel(props: {
  s: Platsinstallningar;
  nu: number;
  spara: (sektion: string, gor: () => Promise<Platsinstallningar>) => Promise<boolean>;
  beskedrad: ComponentChildren;
}) {
  const g = props.s.givare;
  const [val, setVal] = useState<Record<string, { roll: Givarroll | ""; namn: string }>>({});
  if (!g) return null;
  const id = props.s.plats.id;
  const valda = new Map(g.valda.map((v) => [v.id, v]));
  const lista = g.lista ?? [];
  // Valda givare som navet inte hör just nu visas också, så att de kan tas bort.
  const ohorda = g.valda.filter((v) => !lista.some((h) => h.id === v.id));
  const ett = (gid: string) =>
    val[gid] ?? { roll: valda.get(gid)?.roll ?? "", namn: valda.get(gid)?.namn ?? "" };
  const satt = (gid: string, ny: Partial<{ roll: Givarroll | ""; namn: string }>) =>
    setVal((x) => ({ ...x, [gid]: { ...ett(gid), ...ny } }));

  return (
    <Kort rubrik="Givare" bred>
      <p class="liten">
        Ruuvi-givarna som navet hör, de starkaste först. Navet skickar bara värden från givarna som
        har en plats här, aldrig från grannarnas.
        {g.listaTid !== null ? ` Listan kom ${sedan(g.listaTid, props.nu)}.` : ""}
      </p>
      {g.bluetooth === false && (
        <p class="meddelande">Navet har ingen Bluetooth. Se navets logg.</p>
      )}
      {g.lista === null && <p class="meddelande">Navet har inte skickat någon lista än.</p>}
      {g.lista !== null && lista.length === 0 && (
        <p class="meddelande">Navet hör inga Ruuvi-givare just nu.</p>
      )}
      <ul class="givarlista">
        {lista.map((h) => {
          const v = ett(h.id);
          const vald = valda.get(h.id);
          return (
            <li key={h.id} class="givare" aria-label={givarnamn(h.modell, h.id)}>
              <Rad namn={`${givarnamn(h.modell, h.id)}${h.modell ? ` · ${h.modell}` : ""}`}>
                {vald ? givarensPlats(vald) : "ingen plats"}
              </Rad>
              <p class="liten">
                {[
                  h.nyckelSaknas ? "krypterad, nyckeln saknas på navet" : null,
                  h.temp !== null ? `${tal(h.temp, 1)} °C` : null,
                  h.fukt !== null ? `${tal(h.fukt, 0)} %` : null,
                  `signal ${SIGNALSTYRKA(h.signal)}`,
                  h.batteri !== null ? `batteri ${tal(h.batteri, 2)} V` : null,
                  typeof h.batteriProcent === "number"
                    ? `batteri ${tal(h.batteriProcent, 0)} %`
                    : null,
                  `hördes ${sedan(h.senast, props.nu)}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              <div class="knapprad">
                <label>
                  Plats
                  <select
                    value={v.roll}
                    onChange={(e) => satt(h.id, { roll: e.currentTarget.value as Givarroll | "" })}
                  >
                    <option value="">Välj plats</option>
                    {g.roller.map((r) => (
                      <option key={r} value={r}>
                        {GIVARROLLNAMN[r]}
                      </option>
                    ))}
                  </select>
                </label>
                {v.roll === "rum" && (
                  <label>
                    Rummets namn
                    <input
                      autoComplete="off"
                      maxLength={40}
                      value={v.namn}
                      placeholder="Till exempel Loft"
                      onInput={(e) => satt(h.id, { namn: e.currentTarget.value })}
                    />
                  </label>
                )}
                <button
                  type="button"
                  class="knapp-huvud"
                  disabled={v.roll === ""}
                  onClick={() =>
                    v.roll &&
                    props.spara("givare", () =>
                      api.valjGivare(
                        id,
                        h.id,
                        v.roll as Givarroll,
                        v.roll === "rum" ? v.namn : null,
                      ),
                    )
                  }
                >
                  Spara {givarnamn(h.modell, h.id)}
                </button>
                {vald && (
                  <button
                    type="button"
                    class="knapp-andra"
                    onClick={() => props.spara("givare", () => api.taBortGivare(id, h.id))}
                  >
                    Ta bort platsen
                  </button>
                )}
              </div>
            </li>
          );
        })}
        {ohorda.map((v) => (
          <li key={v.id} class="givare" aria-label={givarnamn(v.modell, v.id)}>
            <Rad namn={givarnamn(v.modell, v.id)}>{givarensPlats(v)}, hörs inte just nu</Rad>
            <div class="knapprad">
              <button
                type="button"
                class="knapp-andra"
                onClick={() => props.spara("givare", () => api.taBortGivare(id, v.id))}
              >
                Ta bort platsen
              </button>
            </div>
          </li>
        ))}
      </ul>
      <p class="liten">
        Hörs en givare dåligt (signal under −85 dBm): flytta givaren eller navet närmare, eller bort
        från metall och väggar.
      </p>
      {lista.some((h) => h.nyckelSaknas) && (
        <p class="liten">
          En krypterad givare (BTHome) behöver sin nyckel på navet: sudo batkoll-nav givare nyckel
          och de fyra tecknen, på Pi:n. Nyckeln lämnar aldrig navet.
        </p>
      )}
      {props.beskedrad}
    </Kort>
  );
}

/** b9: badtemperaturen, och vilken källa den kommer från. */
export function Badtemperaturkort(props: { b: Badtemperatur; tidszon: string; nu: number }) {
  const { b, tidszon, nu } = props;
  // b11 (BK31): ett beräknat värde är ett dygnsvärde, med källan som licensen kräver.
  const beraknad = b.kalla === "beraknad";
  const dygnet = new Date(b.tid).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
  return (
    <Kort rubrik="Badtemperatur">
      <div class="stort">
        {tal(b.temp, 1)} °C
        {beraknad && <span class="liten">beräknad, inte uppmätt</span>}
      </div>
      <p class="liten">
        {b.text} · {beraknad ? `dygnet ${dygnet}` : dagOchTid(b.tid, tidszon, nu)}
      </p>
      {b.upphov && <p class="liten upphov">{b.upphov}</p>}
    </Kort>
  );
}
